import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { getGPUTier } from 'detect-gpu';
import { ClipboardService } from 'ngx-clipboard';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, from, of, ReplaySubject, Subject } from 'rxjs';
import { concatMap, filter, pairwise, switchMap, takeUntil, tap } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable, coreSelectors } from 'app/core/state';
import { isInDev } from 'app/core/utilities/env-util';
import { LocalStorageService } from 'app/services/localStorage.service';
import { Timeout, WindowWithWebkitAudio } from 'app/types';
import assertNever from 'app/utils/assert-never';
import { Loggable } from 'app/utils/logging/loggable';
import { areDevicesEqual } from 'app/utils/media/device';
import { cleanTracks, DUMMY_VIDEO_STREAM, getActiveStreams, getUserMedia } from 'app/utils/media/stream';
import { notUndefined } from 'app/utils/stream-util';

import { DEFAULT_FRAME_RATE } from '../../constants/video';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../../modal-simple-social/modal-simple-social.component';
import SoundControl from '../../sound-control';
import { socialAppActions, socialAppSelectors } from '../../state/app';
import {
  actions,
  selectors,
  initialMediaState,
  MediaState,
  SimpleMediaDeviceInfo,
  mediaSelectors,
  mediaActions,
} from '../../state/media';
import { DEVICE_NOT_SUPPORTED_TEXT, DEVICE_NOT_SUPPORTED_TITLE, GET_IOS_APP_TEXT, IOS_APP_URL } from '../../strings';
import { RESOLUTION_1080P, RESOLUTION_STANDARD, VideoResolution } from '../../types/media';
import { BrowserCheck, BrowserService } from '../browser.service';
import { VideoChatService } from '../videochat.service';

import { VideoEffectsProcessor } from './video-effects-processor';
import { HelpCenterUrls } from 'app/utils/help-center';

export type Devices = MediaDeviceInfo[];

enum DeviceKind {
  camera = 'videoinput',
  mic = 'audioinput',
  speaker = 'audiooutput',
}

enum MediaChangeType {
  FIRST_DEVICE_ADDED = 'FIRST_DEVICE_ADDED',
  NEW_DEVICE_CONNECTED = 'NEW_DEVICE_CONNECTED',
  ACTIVE_DEVICE_DISCONNECTED = 'ACTIVE_DEVICE_DISCONNECTED',
  INACTIVE_DEVICE_DISCONNECTED = 'INACTIVE_DEVICE_DISCONNECTED',
  DEFAULT_CHANGED = 'DEFAULT_CHANGED',
  NOT_CHANGED = 'NOT_CHANGED',
}

const findDifferenceBetweenArrays = (array1: Devices, array2: Devices): Devices => {
  const difference: Devices = [];
  array1.forEach((d) => {
    let found = false;
    array2.forEach((nd) => {
      if (!found) {
        found = d.deviceId === nd.deviceId;
      }
    });
    if (!found) {
      difference.push(d);
    }
  });
  return difference;
};

const getDevicesOfKind = ({ devices, kind }: { kind: string; devices: MediaDeviceInfo[] }): MediaDeviceInfo[] =>
  devices.filter(({ kind: deviceKind }) => deviceKind === kind);

/**
 * DeviceService
 *
 * This service manages external devices, such as cameras, speakers, and microphones
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceService extends Loggable {
  private browserService = inject(BrowserService);
  private videoChatService = inject(VideoChatService);
  private localStorageService = inject(LocalStorageService);
  private toasterService = inject(ToastrService);
  private clipBoardService = inject(ClipboardService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private store = inject(Store);

  private _cameraStream?: MediaStream;
  private _audioStream?: MediaStream;
  private _echoAudioStream?: MediaStream;
  private analyserStream?: MediaStream;
  private rawCameraStream?: MediaStream;
  private hdCameraStream?: MediaStream;

  private audioStreamSource = new BehaviorSubject<MediaStream | undefined>(undefined);
  public audioStream$ = this.audioStreamSource.asObservable();

  private echoAudioStreamSource = new Subject<MediaStream | undefined>();
  public echoAudioStream$ = this.echoAudioStreamSource.asObservable();

  private cameraStreamSource = new BehaviorSubject<MediaStream | undefined>(undefined);
  public cameraStream$ = this.cameraStreamSource.asObservable();

  private rawCameraStreamSource = new BehaviorSubject<MediaStream | undefined>(undefined);
  public rawCameraStream$ = this.rawCameraStreamSource.asObservable();

  private hdCameraStreamSource = new BehaviorSubject<MediaStream | undefined>(undefined);
  public hdCameraStream$ = this.hdCameraStreamSource.asObservable();
  private optimizingVideo = false;

  public get audioStream(): MediaStream | undefined {
    return this._audioStream;
  }
  public set audioStream(newStream: MediaStream | undefined) {
    this.audioStreamSource.next(newStream);
    this._audioStream = newStream;
  }

  public get echoAudioStream(): MediaStream | undefined {
    return this._echoAudioStream;
  }
  public set echoAudioStream(newStream: MediaStream | undefined) {
    this.echoAudioStreamSource.next(newStream);
    this._echoAudioStream = newStream;
  }

  public get cameraStream(): MediaStream | undefined {
    return this._cameraStream;
  }
  public set cameraStream(newStream: MediaStream | undefined) {
    if (!newStream) {
      return;
    }
    this.rawCameraStream = newStream;
    this.rawCameraStreamSource.next(newStream);
    this.info('DeviceService: videoEffectsProcessor available?', {
      isProcessorAvailable: !!this.videoEffectsProcessor,
    });
    const modifiedStream = newStream ? this.videoEffectsProcessor?.processVideoStream(newStream) : undefined;
    this.info('DeviceService: newStream, modifiedStream, and what is emitted to cameraStreamSource', {
      newStreamId: newStream?.id,
      modifiedStreamId: modifiedStream?.id,
    });
    this.cameraStreamSource.next(modifiedStream);

    this._cameraStream = modifiedStream;
  }

  // Used to track all Available Devices
  private devices: MediaDeviceInfo[] = [];
  private previousDevices: MediaDeviceInfo[] = [];
  public mics: MediaDeviceInfo[] = [];
  public cameras: MediaDeviceInfo[] = [];
  public speakers: MediaDeviceInfo[] = [];

  // Used to track current Device Selected
  public camera?: MediaDeviceInfo;
  public mic?: MediaDeviceInfo;
  public speaker?: MediaDeviceInfo;

  // Device Kind variables used in the web api
  public cameraKind = 'videoinput';
  public micKind = 'audioinput';
  public speakerKind = 'audiooutput';

  // permission states
  public audioGranted = false;
  public videoGranted = false;

  // Used to check when page loads for the first time to avoid init devices until permissions are checked
  public initialized = false;

  public isDeviceAvailable = true;

  // Used to detect sound input
  private dataArray = new Uint8Array(0);
  private audioContext: AudioContext | undefined = undefined;
  private soundAnalyser: AnalyserNode | undefined = undefined;
  private soundInputTimer: Timeout | undefined = undefined;
  private gainRevertTimer: Timeout | undefined = undefined;
  private isGainControlLoopEnabled = true;

  private gainNode: GainNode | undefined = undefined;
  private gainStreamSourceNode: MediaStreamAudioSourceNode | undefined = undefined;
  private gainStreamDestinationNode: MediaStreamAudioDestinationNode | undefined = undefined;
  private gainValue = 1;
  private frameRate = 18;
  private destroyed$ = new ReplaySubject<boolean>(1);

  // State from ngrx
  private presenterResolution: VideoResolution = RESOLUTION_STANDARD;
  private mediaState: MediaState = initialMediaState;
  private isTestUser = false;
  private isPresenting = false;
  private isVideoEnabled = true;

  private videoEffectsProcessor?: VideoEffectsProcessor;
  private guestFrameRate?: number;

  private isInSocial = false;

  public constructor() {
    super();
    this.setDisplayName('DeviceService');
    void this.browserService.checkBrowserAvailability();
  }

  public init(): void {
    this.destroyed$ = new ReplaySubject(1);

    this.videoEffectsProcessor = new VideoEffectsProcessor(this.browserService.browserType, this.store);

    if (navigator?.mediaDevices) {
      navigator.mediaDevices.ondevicechange = () => this.onDeviceChange();
    }

    this.store
      .select(socialAppSelectors.isInSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isInSocial) => (this.isInSocial = isInSocial));

    this.store
      .select(selectors.getMediaState)
      .pipe(
        filter((avState) => !!avState),
        takeUntil(this.destroyed$),
      )
      .subscribe((avState) => {
        this.mediaState = avState ?? this.mediaState;
        // Sets isVideoEnabled when reentering room
        this.isVideoEnabled = this.mediaState.videoEnabled;
      });

    // adjust video quality when presenting
    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(
        pairwise(),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(([previousIsPresenting, isPresenting]) => {
          if (!this.isInSocial) {
            return;
          }
          this.isPresenting = isPresenting;
        }),
        switchMap(([previousIsPresenting, isPresenting]) => {
          if (previousIsPresenting !== isPresenting && this.isInSocial) {
            return from(this.optimizeVideo());
          }
          return of(null);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.store
      .select(coreSelectors.isTestUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isTestUser) => (this.isTestUser = isTestUser));

    this.store
      .select(mediaSelectors.getVideoEnabled)
      .pipe(
        pairwise(),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        tap(([previousVideoEnabled, isVideoEnabled]) => {
          this.isVideoEnabled = isVideoEnabled;
        }),
        switchMap(async ([previousVideoEnabled, isVideoEnabled]) => {
          // on mute toggle for video, ensure we drop or recreate the video stream. This way, we don't keep the camera
          // light on even when the user has a muted camera
          if (previousVideoEnabled !== isVideoEnabled) {
            if (isVideoEnabled) {
              this.hdCameraStream = await this.createVideoStream(this.camera?.deviceId, true);
              this.cameraStream = await this.createVideoStream(this.camera?.deviceId);
              this.hdCameraStreamSource.next(this.hdCameraStream);
            } else {
              await cleanTracks(this.cameraStream, this);
              await cleanTracks(this.rawCameraStream, this);
              await cleanTracks(this.hdCameraStream, this);
              this.cameraStream = DUMMY_VIDEO_STREAM;
            }
          }
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
    // Customer API Video FPS
    this.store
      .select(socialAppSelectors.getGuestVideoFPS)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((fps) => {
        this.guestFrameRate = fps;
      });

    this.store
      .select(coreSelectors.isAuthenticated)
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        concatMap(() => this.videoChatService.getFrameRate()),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (frameRate) => {
          if (typeof this.guestFrameRate === 'number') {
            this.info('Setting frame rate', this.guestFrameRate);
            this.frameRate = (this.guestFrameRate as number) ?? DEFAULT_FRAME_RATE;
            return;
          }
          this.info('Setting frame rate', { frameRate });
          this.frameRate = (frameRate as number) ?? DEFAULT_FRAME_RATE;
        },
        error: (err: unknown) => {
          this.error('failed to fetch frame rate', err);
          this.frameRate = DEFAULT_FRAME_RATE;
        },
      });

    this.store
      .select(socialAppSelectors.getVideoEffect)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((videoEffects) => this.videoEffectsProcessor?.setEffects(videoEffects));

    // adjust video quality as the quality setting changes
    this.store
      .select(socialAppSelectors.getPresenterResolution)
      .pipe(
        filter(() => this.isInSocial),
        tap((resolution: VideoResolution) => {
          this.presenterResolution = resolution;
        }),
        switchMap(() => from(this.optimizeVideo())),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_gain_control)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isGainControlLoopEnabled) => (this.isGainControlLoopEnabled = isGainControlLoopEnabled));
  }

  private async onDeviceChange() {
    if (!this.initialized) {
      return;
    }
    this.info('onDeviceChange Media devices changed');
    try {
      await this.initializeDevices({
        audio: this.audioGranted,
        video: this.videoGranted,
      });
    } catch (e) {
      this.error('Failed to initialize devices', e);
    }
  }

  public async reInitializeAudio(): Promise<void> {
    // Reset microphone selection after permission changed
    // Clean tracks
    await this.cleanAudioStreamsAndTracks();

    // Reset the devices and reselect a microphone to unstuck audio
    this.setMics();
    const currentMicId = await this.getCurrentAudio();

    if (currentMicId) {
      const mic = this.mics.find((device) => device.deviceId === currentMicId) ?? this.mics[0];
      if (mic) {
        this.info('Change mic device', { mic });
        await this.changeMicDevice(mic);
      }
    }
  }

  public async initializeDevices({ audio, video }: { audio: boolean; video: boolean }): Promise<void> {
    this.initialized = true;
    if (this.isTestUser) {
      this.audioStream = await this.createAudioStream();
      this.cameraStream = await this.createVideoStream();
      this.info('Test User Streams - Video and Audio: ', {
        audioStream: this.audioStream,
        videoStream: this.cameraStream,
      });
      this.audioGranted = true;
      this.videoGranted = true;
      return;
    }

    this.audioGranted = audio;
    this.videoGranted = video;

    // Used to track all devices globally
    const newDevices = await navigator.mediaDevices.enumerateDevices();

    // If the new devices are the same from the current ones don't proceed
    if (JSON.stringify(newDevices) === JSON.stringify(this.devices)) {
      this.trace('Devices are the same', newDevices, this.devices);
      return;
    }

    this.info('Devices are not the same. New Devices To Be Set: ', newDevices);
    this.previousDevices = [...this.devices];
    this.devices = newDevices;

    if (audio) {
      const micChange = this.getDeviceChangeType(DeviceKind.mic, this.mic);
      switch (micChange) {
        case MediaChangeType.ACTIVE_DEVICE_DISCONNECTED:
        case MediaChangeType.DEFAULT_CHANGED:
        case MediaChangeType.FIRST_DEVICE_ADDED: {
          this.info('Active mic was changed, setting it as the current device');
          this.setMics();
          const currentMicId = await this.getCurrentAudio();

          if (currentMicId) {
            const mic = this.mics.find((device) => device.deviceId === currentMicId) ?? this.mics[0];
            if (mic) {
              this.info('Change mic device', { mic });
              await this.changeMicDevice(mic);
            }
          }
          break;
        }

        case MediaChangeType.INACTIVE_DEVICE_DISCONNECTED: {
          this.info('Inactive mic was disconnected');
          this.setMics();
          break;
        }

        case MediaChangeType.NEW_DEVICE_CONNECTED: {
          this.info('New mic was added');
          this.setMics();
          break;
        }

        case MediaChangeType.NOT_CHANGED:
          break;

        default:
          assertNever(micChange);
      }

      const speakerChange = this.getDeviceChangeType(DeviceKind.speaker, this.speaker);
      switch (speakerChange) {
        case MediaChangeType.ACTIVE_DEVICE_DISCONNECTED:
        case MediaChangeType.DEFAULT_CHANGED:
        case MediaChangeType.FIRST_DEVICE_ADDED: {
          this.info('Active speaker was changed, setting it as the current device');
          this.setSpeakers();
          const currentSpeakerId = await this.getCurrentAudio();

          if (currentSpeakerId) {
            const speaker = this.speakers.find((device) => device.deviceId === 'default') ?? this.speakers[0];
            if (speaker) {
              this.info('Change speaker device', { speaker });
              this.changeSpeakerDevice(speaker);
            }
          }
          break;
        }

        case MediaChangeType.INACTIVE_DEVICE_DISCONNECTED: {
          this.info('Inactive speaker was disconnected');
          this.setSpeakers();
          break;
        }

        case MediaChangeType.NEW_DEVICE_CONNECTED: {
          this.info('New speaker was added');
          this.setSpeakers();
          break;
        }

        case MediaChangeType.NOT_CHANGED:
          break;

        default:
          assertNever(speakerChange);
      }
    }

    if (video) {
      const cameraChange = this.getDeviceChangeType(DeviceKind.camera, this.camera);
      switch (cameraChange) {
        case MediaChangeType.ACTIVE_DEVICE_DISCONNECTED:
        case MediaChangeType.DEFAULT_CHANGED:
        case MediaChangeType.FIRST_DEVICE_ADDED: {
          this.info('Active camera was changed, setting it as the current device');
          this.setCameras();
          const currentCameraId = await this.getCurrentCamera();
          const camera = this.cameras.find((device) => device.deviceId === currentCameraId) ?? this.cameras[0];

          if (camera) {
            await this.changeCameraDevice(camera);
          } else {
            await cleanTracks(this.cameraStream, this);
            this.cameraStream = undefined;
            this.store.dispatch(actions.CAMERA_SELECTED({ camera: null }));
          }
          break;
        }

        case MediaChangeType.INACTIVE_DEVICE_DISCONNECTED: {
          this.info('An Inactive camera was disconnected');
          this.setCameras();
          break;
        }

        case MediaChangeType.NEW_DEVICE_CONNECTED: {
          this.info('A new camera was added');
          this.setCameras();
          break;
        }

        case MediaChangeType.NOT_CHANGED:
          break;

        default:
          assertNever(cameraChange);
      }
    }
  }

  private getDeviceChangeType(kind: DeviceKind, currentDevice: MediaDeviceInfo | undefined): MediaChangeType {
    const newDevices = getDevicesOfKind({ kind, devices: this.devices });
    const oldDevices = getDevicesOfKind({ kind, devices: this.previousDevices });

    if (newDevices.length > 0 && oldDevices.length === 0) {
      return MediaChangeType.FIRST_DEVICE_ADDED;
    }

    const oldDefault = oldDevices.find((device) => device.kind === kind && device.deviceId === 'default');
    const newDefault = newDevices.find((device) => device.kind === kind && device.deviceId === 'default');
    if (oldDefault && !areDevicesEqual(oldDefault, newDefault) && currentDevice?.deviceId === 'default') {
      return MediaChangeType.DEFAULT_CHANGED;
    }

    if (newDevices.length === oldDevices.length) {
      const difference = findDifferenceBetweenArrays(oldDevices, newDevices);
      if (difference.length) {
        return MediaChangeType.NEW_DEVICE_CONNECTED;
      }
    }

    if (newDevices.length > oldDevices.length) {
      return MediaChangeType.NEW_DEVICE_CONNECTED;
    }

    if (newDevices.length < oldDevices.length) {
      const difference = findDifferenceBetweenArrays(oldDevices, newDevices);
      if (
        difference.find(
          (device) => device.deviceId === currentDevice?.deviceId && device.label === currentDevice?.label,
        )
      ) {
        return MediaChangeType.ACTIVE_DEVICE_DISCONNECTED;
      }

      return MediaChangeType.INACTIVE_DEVICE_DISCONNECTED;
    }

    return MediaChangeType.NOT_CHANGED;
  }

  // Set Devices
  private setCameras() {
    this.cameras = getDevicesOfKind({ kind: DeviceKind.camera, devices: this.devices });
    const cameras = this.cameras.map(
      ({ deviceId, label }: MediaDeviceInfo): SimpleMediaDeviceInfo => ({ deviceId, label }),
    );
    this.store.dispatch(actions.AVAILABLE_CAMERAS_CHANGED({ cameras }));
  }

  private setMics() {
    this.mics = getDevicesOfKind({ kind: DeviceKind.mic, devices: this.devices });
    const mics = this.mics.map(({ deviceId, label }: MediaDeviceInfo): SimpleMediaDeviceInfo => ({ deviceId, label }));
    this.store.dispatch(actions.AVAILABLE_MICS_CHANGED({ mics }));
    this.checkForSafariFirefox();
  }

  private setSpeakers() {
    this.speakers = getDevicesOfKind({ kind: DeviceKind.speaker, devices: this.devices });
    const speakers = this.speakers.map(
      ({ deviceId, label }: MediaDeviceInfo): SimpleMediaDeviceInfo => ({ deviceId, label }),
    );
    this.store.dispatch(actions.AVAILABLE_SPEAKERS_CHANGED({ speakers }));
    this.checkForSafariFirefox();
  }

  private checkForSafariFirefox(): void {
    if (this.browserService.isSafari || this.browserService.isFirefox) {
      const defaultOutput: MediaDeviceInfo = {
        deviceId: 'default',
        kind: 'audiooutput',
        label: 'System Default Speaker Device',
        groupId: 'default',
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        toJSON: () => {},
      };
      this.speakers = [defaultOutput];
      this.speaker = defaultOutput;
      this.store.dispatch(actions.AVAILABLE_SPEAKERS_CHANGED({ speakers: [defaultOutput] }));
      this.store.dispatch(actions.SPEAKER_SELECTED({ speaker: defaultOutput }));
    }
  }

  /* Getting Current Camera and mic Streams and Tracks */
  private async getCurrentCamera(): Promise<string | undefined> {
    let camera: string | undefined;
    try {
      const stream = await getUserMedia(
        { video: { width: { max: RESOLUTION_1080P.width }, height: { max: RESOLUTION_1080P.height } } },
        this,
      );
      camera = stream.getVideoTracks()[0]?.getSettings().deviceId;
      await cleanTracks(stream);
    } catch (error) {
      this.error('Failed to get current Video on init with error: ', error);
    }
    return camera;
  }

  private async getCurrentAudio(): Promise<string | undefined> {
    let audio: string | undefined;
    try {
      const stream = await getUserMedia(
        {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: this.enableBrowserAutoGainControl,
          },
        },
        this,
      );
      audio = stream.getAudioTracks()[0]?.getSettings().deviceId;
      await cleanTracks(stream);
    } catch (error) {
      this.error('Failed to get current Audio on init with error: ', error);
    }
    return audio;
  }

  private getVideoConstraints(deviceId?: string, hd?: boolean): MediaTrackConstraints {
    let width: MediaTrackConstraints['width'] = 220;
    let height: MediaTrackConstraints['height'] = 220;

    const resolution = hd ? RESOLUTION_1080P : this.presenterResolution;

    if (hd || this.isPresenting) {
      width = { ideal: resolution.width, max: resolution.width };
      height = { ideal: resolution.height, max: resolution.height };
    }

    const constraints: MediaTrackConstraints = { width, height, frameRate: { ideal: this.frameRate } };
    const exact = deviceId || this.camera?.deviceId;

    const ret = exact ? { ...constraints, deviceId: { exact } } : constraints;
    this.info('getVideoConstraints', ret);
    // if we don't override the aspect ratio, the camera will be stretched if high CPU load
    return { ...ret, aspectRatio: 1 };
  }

  private async optimizeVideo(): Promise<void> {
    if (this.optimizingVideo) {
      return;
    }
    this.optimizingVideo = true;
    this.info('Optimizing video', { isPresenting: this.isPresenting });
    const { deviceId } = this.camera ?? {};
    if (!deviceId) {
      this.info('Cannot optimize video, user has no camera device');
      return;
    }
    await this.changeCameraDevice({ deviceId });
    this.optimizingVideo = false;
  }

  /* switching mics, cameras and speakers */

  public async handleDeviceChange(device: SimpleMediaDeviceInfo | undefined, kind: string): Promise<void> {
    if (!device) {
      return;
    }
    const { deviceId } = device;
    if (kind === this.cameraKind) {
      await this.changeCameraDevice({ deviceId });
    }
    if (kind === this.micKind) {
      await this.changeMicDevice({ deviceId });
    }
    if (kind === this.speakerKind) {
      this.changeSpeakerDevice({ deviceId });
    }
  }

  public async changeCameraDevice({ deviceId }: { deviceId: string }): Promise<void> {
    const badCameras = this.getBadCameras();
    const camera = this.cameras.find((c) => c.deviceId === deviceId);
    if (!camera) {
      this.error('Tried to switch to invalid camera ID', { deviceId, cameras: this.cameras });
      return;
    }

    const { label } = camera;
    this.camera = camera;
    this.store.dispatch(actions.CAMERA_SELECTED({ camera: { deviceId, label } }));
    this.info(`Changing to Camera ${label} with id ${deviceId}`);

    // Clean Previous Video Streams
    await this.cleanVideoStreamsAndTracks();

    try {
      if (badCameras.includes(deviceId)) {
        this.warn('Camera matches bad camera list, simulating failure');
        throw new Error('Simulating unreadable camera');
      }
      // Only for waiting room
      const hdStream = await this.createVideoStream(camera.deviceId, true);
      const cameraStream = await this.createVideoStream(camera.deviceId);
      const videoTracks = cameraStream.getVideoTracks();
      const track = videoTracks[0];

      const { enabled, id, kind, label: newLabel, muted, readyState } = track;
      this.info('Received video track', {
        ...track.getSettings(),
        id,
        kind,
        label: newLabel,
        readyState,
        enabled,
        muted,
      });

      track.enabled = this.mediaState.videoEnabled;
      this.cameraStream = cameraStream;
      this.hdCameraStream = hdStream;
      this.hdCameraStreamSource.next(this.hdCameraStream);
      this.store.dispatch(mediaActions.CAMERA_READABLE({ deviceId: camera.deviceId, readable: true }));
    } catch (error) {
      this.error(`Error Changing Camera ${camera?.label} with id ${camera?.deviceId}`, error);
      this.store.dispatch(mediaActions.CAMERA_READABLE({ deviceId: camera.deviceId, readable: false }));
    }
  }

  public async changeMicDevice({ deviceId }: { deviceId: string }): Promise<void> {
    const previousMic = this.mic;
    const mic = this.mics.find((c) => c.deviceId === deviceId);
    if (!mic) {
      this.error('Tried to switch to invalid mic ID', { deviceId, mics: this.mics });
      return;
    }

    this.mic = mic;
    const { label } = mic;
    this.store.dispatch(actions.MIC_SELECTED({ mic: { deviceId, label } }));
    this.info(`Changing to Mic ${label} with id ${deviceId}`);
    try {
      // Clean Previous Video Streams
      await this.cleanAudioStreamsAndTracks();
      // Create new local Track
      this.audioStream = await this.createAudioStream(deviceId);
      this.setupGainNode(this.audioStream);
      const audioTrack = this.audioStream.getAudioTracks();
      audioTrack[0].enabled = this.mediaState.audioEnabled;
      await this.setEcho(deviceId);
      await this.initSoundAnalyser();
    } catch (error) {
      this.error(`Error Changing Mic ${label} with id ${deviceId}`, error);
      // Reset mic back to the previous mic
      if (previousMic) {
        await this.changeMicDevice({ deviceId: previousMic.deviceId });
      }
    }
  }

  public changeSpeakerDevice({ deviceId }: { deviceId: string }): void {
    if (this.browserService.isSafari) {
      return;
    } // Safari doesn't support speaker change

    const speaker = this.speakers.find((c) => c.deviceId === deviceId);
    if (!speaker) {
      this.error('Tried to switch to invalid speaker ID', { deviceId, speakers: this.speakers });
      return;
    }
    const { label } = speaker;
    this.speaker = speaker;
    this.store.dispatch(actions.SPEAKER_SELECTED({ speaker: { deviceId, label } }));

    this.info(`Changing to Speaker ${label} with id ${deviceId}`);
    // TODO: SoundControl should be instantiated at this point, but potential race condition?
    const soundControl = SoundControl.getInstance();
    if (soundControl) {
      soundControl.audioElementSinkId = deviceId;
    } else {
      this.error('SoundControl not instantiated');
    }
  }

  private async createVideoStream(inputId?: string, hd?: boolean): Promise<MediaStream> {
    if (!this.isVideoEnabled) {
      return DUMMY_VIDEO_STREAM;
    }

    const video = this.getVideoConstraints(inputId, hd);
    return await getUserMedia({ video }, this);
  }

  private async createAudioStream(inputId?: string): Promise<MediaStream> {
    const audio = {
      deviceId: {
        exact: inputId,
      },
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: this.enableBrowserAutoGainControl,
    };

    const stream = await getUserMedia({ audio }, this);
    return stream;
  }

  private get enableBrowserAutoGainControl(): boolean {
    return !this.isGainControlLoopEnabled || this.browserService.isSafari;
  }

  /* echo ring set when new mic is selected */

  private async setEcho(mic: string): Promise<void> {
    this.echoAudioStream = await this.createAudioStream(mic);

    if (!this.echoAudioStream) {
      this.error('Failed to create Echo Audio Stream');
    }
  }

  /* Sound Detector */
  public async initSoundAnalyser(): Promise<void> {
    if (this.enableBrowserAutoGainControl || this.isTestUser) {
      this.info('Disabling manual gain control loop');
      return;
    }
    // Initiate audioContext, soundAnalyser and dataArray
    window.AudioContext = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;
    this.audioContext = new AudioContext();
    this.soundAnalyser = this.audioContext.createAnalyser();
    this.soundAnalyser.fftSize = 32;
    this.dataArray = new Uint8Array(this.soundAnalyser.frequencyBinCount);
    await cleanTracks(this.analyserStream, this);
    this.analyserStream = await this.createAudioStream(this.mic?.deviceId);
    this.audioContext.createMediaStreamSource(this.analyserStream).connect(this.soundAnalyser);

    if (this.soundInputTimer) {
      clearInterval(this.soundInputTimer);
    }
    this.soundInputTimer = setInterval(() => this.setGain(), 250);
  }

  private setGain(): void {
    // don't run if no audio context
    if (!this.audioContext) {
      return;
    }

    // don't run if mic is muted
    if (!this.mediaState.audioEnabled) {
      return;
    }

    // Calculate average of this run
    this.soundAnalyser?.getByteTimeDomainData(this.dataArray);
    let diffSum = 0;
    this.dataArray.forEach((value) => {
      diffSum += Math.abs(128 - value);
    });
    const diffAvg = diffSum / this.dataArray.length;

    // Turn up gain
    if (diffAvg > 4 && this.gainNode) {
      this.setGainValue(1);

      // Set/Reset timer that will turn down volume again in N seconds
      if (this.gainRevertTimer) {
        clearTimeout(this.gainRevertTimer);
      }
      this.gainRevertTimer = setTimeout(() => {
        this.setGainValue(0.2);
      }, 4000);

      // Initial gain lowered
    } else if (!this.gainRevertTimer && this.gainNode) {
      this.setGainValue(0.2);
    }
  }

  /* clean up functions for tracks and streams */
  private async cleanAudioStreamsAndTracks(): Promise<void> {
    await cleanTracks(this.audioStream, this);
    this.audioStream = undefined;

    await cleanTracks(this.echoAudioStream, this);
    this.echoAudioStream = undefined;

    await cleanTracks(this.analyserStream, this);
    this.analyserStream = undefined;

    await cleanTracks(this.gainStreamSourceNode?.mediaStream, this);
    this.gainStreamSourceNode = undefined;

    await cleanTracks(this.gainStreamDestinationNode?.stream, this);
    this.gainStreamDestinationNode = undefined;

    this.gainNode = undefined;
  }

  private async cleanVideoStreamsAndTracks(): Promise<void> {
    this.info('cleanVideoStreamsAndTracks', this.cameraStream?.getVideoTracks()[0].label);

    await cleanTracks(this.rawCameraStream, this);
    this.rawCameraStream = undefined;

    await cleanTracks(this.hdCameraStream, this);
    this.hdCameraStream = undefined;

    await cleanTracks(this.cameraStream, this);
    this.cameraStream = undefined;

    const activeStreams = getActiveStreams();
    for (const key in activeStreams) {
      const stream = activeStreams[key] as MediaStream;
      const track = stream.getTracks();
      if (track[0].kind !== 'audio') {
        await cleanTracks(stream, this);
      }
    }

    this.info('cleanVideoStreamsAndTracks done');
  }

  public async isGPUHigh(): Promise<boolean> {
    try {
      if (this.isTestUser) {
        return true;
      }

      const gpuTier = await getGPUTier({ failIfMajorPerformanceCaveat: false });
      this.info(`GPU Tier: ${gpuTier.tier} with object: `, gpuTier);

      return gpuTier.tier > 0;
    } catch (e) {
      const { message } = e as { message: string };
      this.error('Failed to get gpu', message);
    }
    return false;
  }

  private setupGainNode(stream: MediaStream): void {
    if (this.browserService.isSafari) {
      return;
    }

    window.AudioContext = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;

    // We assume only one audio track per stream
    const audioTrack = stream.getAudioTracks()[0];

    const aCtx = new AudioContext();
    this.gainStreamSourceNode = aCtx.createMediaStreamSource(new MediaStream([audioTrack]));
    this.gainStreamDestinationNode = aCtx.createMediaStreamDestination();
    this.gainNode = aCtx.createGain();
    this.gainNode.gain.value = this.gainValue;
    this.gainStreamSourceNode.connect(this.gainNode);
    this.gainNode.connect(this.gainStreamDestinationNode);

    stream.removeTrack(audioTrack);
    stream.addTrack(this.gainStreamDestinationNode.stream.getAudioTracks()[0]);
  }

  private setGainValue(gainValue: number): void {
    if (!this.gainNode) {
      return;
    }

    this.gainValue = gainValue;
    this.gainNode.gain.value = gainValue;
  }

  public async destroy(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    navigator.mediaDevices.ondevicechange = () => {};

    // cleaning and turning off all tracks / streams
    await this.cleanAudioStreamsAndTracks();
    await this.cleanVideoStreamsAndTracks();

    // resetting all device trackers
    this.devices = [];
    this.mics = [];
    this.cameras = [];
    this.speakers = [];

    // resetting current devices
    this.camera = undefined;
    this.mic = undefined;
    this.speaker = undefined;

    // resetting permission states
    this.audioGranted = false;
    this.videoGranted = false;
    this.isTestUser = false;

    this.videoEffectsProcessor?.destroy();
    this.videoEffectsProcessor = undefined;

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public toggleVideoEffectsEnabled(enabled: boolean): void {
    this.info('User toggled video effects', { enabled });
    this.store.dispatch(socialAppActions.TOGGLE_VIDEO_EFFECTS({ enabled }));
  }

  /**
   * Get IDs for cameras that will simulate being unreadable, to test unreadable camera scenarios
   * @returns An array of camera IDs that will simulate being unreadable
   */
  private getBadCameras(): string[] {
    if (!isInDev()) {
      return [];
    }
    const result =
      this.localStorageService.retrieve('badCameras', (raw: string): string[] | undefined => {
        const value = JSON.parse(raw);
        if (Array.isArray(value)) {
          return value;
        }
      }) ?? [];

    if (result.length) {
      this.warn('Simulated bad cameras retrieved', result);
    }
    return result;
  }

  /**
   *
   * Get check object and handles error message to be displayed to the user
   */

  public async checkBrowserAndDevice(check: BrowserCheck): Promise<void> {
    this.isDeviceAvailable = check.isAvailable;

    if (!this.isDeviceAvailable) {
      this.handleDeviceAvailability(check);
      return;
    }

    await this.checkGPUAvailability();
  }

  private handleDeviceAvailability(check: BrowserCheck): void {
    let data;
    if (check.isIOS) {
      data = {
        modalTitle: DEVICE_NOT_SUPPORTED_TITLE,
        text: GET_IOS_APP_TEXT,
        primaryButtonText: 'Get IOS App',
      };
    } else {
      data = {
        modalTitle: DEVICE_NOT_SUPPORTED_TITLE,
        text: DEVICE_NOT_SUPPORTED_TEXT,
        primaryButtonText: 'OK',
        customButtonText: 'Copy Link',
      };
    }
    this.showError(data, !check.isIOS, check.isIOS);
  }

  private async checkGPUAvailability(): Promise<void> {
    const isGPUHigh = await this.isGPUHigh();
    if (!isGPUHigh) {
      const brandName = 'Scoot';
      const data = {
        modalTitle: 'Hardware Acceleration Required',
        text: `Looks like your browser's hardware acceleration may be disabled, or your computer's graphics processor may be unable to handle the ${brandName} Social. The ${brandName} Social requires hardware acceleration. Please turn on this setting following the steps in this help center article and reload the page. If your hardware acceleration is enabled and you are viewing this message then you may experience sub-optimal visual quality.`,
        primaryButtonText: 'Ok',
        customButtonText: 'Go to Help Center',
      };
      this.showError(data, false, false);
    }
  }

  private showError(data: ModalSimpleSocialData, isBrowserError: boolean, isIOS?: boolean): void {
    const dialogRef = this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'primary' && isIOS) {
          const url = IOS_APP_URL;
          window.location.replace(url);
        } else if (isBrowserError) {
          if (result === 'custom') {
            const url = window.location.hostname + window.location.pathname;
            this.clipBoardService.copyFromContent(url);
            this.toasterService.success('Copied to clipboard', 'Success');
          }
          void this.router.navigate(['']);
        } else {
          const url = HelpCenterUrls.HARDWARE_ACCELERATION;
          if (result === 'custom') {
            window.open(url, '_blank');
          }
        }
      });
  }
}
